import { IconType } from 'record-it-components/misc/Icon/Icon';
import {
  IconConfiguration,
  IconConfigurationConstraint,
  IconUtils,
  ReplaceIconConfigurationWithPrefixedVersionDeep
} from './IconUtils';

/**
 * Icons are configured here so they are consistent between components and so they can be easily adapted
 *
 * They also shouldn't be grouped on a component basis, but rather on a responsibility basis.
 * e.g. Icons for controlling the Stopwatch should be put into the `stopwatch` category.
 * Icons for listItems (an icon for expanding a listItem, or entering/navigating into a list item ...) should be put into the `listItem` category
 */
const defaultIconConfigurations = validateCategorizedIconConfigurations({
  page: {
    ConstructionSiteAdminPage: {
      titleIcon: {
        name: 'person-digging',
        type: IconType.Regular
      }
    },
    CreateConstructionSitePage: {
      titleIcon: {
        name: 'person-digging',
        type: IconType.Regular
      }
    },
    CreateWorkTimeEntryPage: {
      titleIcon: {
        name: 'calendar-plus',
        type: IconType.Regular
      }
    },
    CreateWorkTimeEntrySelectActivityPage: {
      titleIcon: {
        name: 'tasks-alt', // TODO: better icon?
        type: IconType.Regular
      }
    },
    CreateWorkTimeEntrySelectConstructionSitePage: {
      titleIcon: {
        name: 'person-digging', // TODO: better icon?
        type: IconType.Regular
      }
    },
    EditWorkTimeEntryPage: {
      titleIcon: {
        name: 'clock',
        type: IconType.Regular
      }
    },
    HomePage: {
      titleIcon: {
        name: 'home',
        type: IconType.Regular
      }
    },
    MyWorkTimeOverviewPage: {
      titleIcon: {
        name: 'clock',
        type: IconType.Regular
      }
    },
    TimeTrackingActivityCompilationAdminPage: {
      titleIcon: {
        name: 'tasks-alt', // TODO: better icon?
        type: IconType.Regular
      }
    },
    TimeTrackingActivityAdminPage: {
      titleIcon: {
        name: 'tasks-alt', // TODO: better icon?
        type: IconType.Regular
      }
    },
    TimeTrackingConfigurationAdminPage: {
      titleIcon: {
        name: 'wrench',
        type: IconType.Regular
      }
    },
    UserAdminPage: {
      titleIcon: {
        name: 'user',
        type: IconType.Regular
      }
    },
    UserGroupAdminPage: {
      titleIcon: {
        name: 'users',
        type: IconType.Regular
      }
    },
    WorkTimeAdminPage: {
      titleIcon: {
        name: 'clock',
        type: IconType.Regular
      }
    }
  },
  listItem: {
    createFromListItem: {
      name: 'plus-circle',
      type: IconType.Regular
    },
    deleteIcon: {
      name: 'trash',
      type: IconType.Regular
    },
    editInDialogIcon: {
      name: 'pen',
      type: IconType.Regular
    },
    editInExtraPageIcon: {
      name: 'edit',
      type: IconType.Regular
    },
    enterListItem: {
      name: 'arrow-right',
      type: IconType.Regular
    }
  },
  stopwatch: {
    pause: {
      name: 'pause',
      type: IconType.Regular
    },
    play: {
      name: 'play',
      type: IconType.Regular
    }
  },
  status: {
    archived: {
      name: 'box-archive',
      type: IconType.Regular
    }
  }
});

export const iconConfigurations: IconConfigurations = {
  default: defaultIconConfigurations,
  prefixed: IconUtils.prefixConfigurations(defaultIconConfigurations)
};

export type IconConfigurations = {
  /**
   * default versions of the configurations in the IconConfiguration format
   */
  default: typeof defaultIconConfigurations;
  /**
   * prefixed versions of the configurations in the PrefixedIconConfiguration format
   * This is useful because a lot of components want the name/type of the icon prefixed with `icon` to prevent naming conflicts.
   * Since it is widely common, we have this version defined here, so you don't always have to manually map the names.
   */
  prefixed: ReplaceIconConfigurationWithPrefixedVersionDeep<
    typeof defaultIconConfigurations
  >;
};

/**
 * Workaround so we have typing support in the definition of the iconConfigurations.
 * Typescript doesn't have support for this yet :(
 */
function validateCategorizedIconConfigurations<
  T extends IconConfigurationConstraint<IconConfiguration>
>(configurations: Readonly<T>): T {
  return configurations;
}
